/**
 * What a dashboard asks of a book of hires.
 *
 * Every figure a partner or an operator reads off an overview — earned this
 * month, how busy the fleet was, which car pays for itself — is the same
 * handful of rollups over the same rows. They live here rather than in a
 * page component for the two reasons domain maths usually does: the partner
 * portal and admin must not each invent their own definition of "occupied",
 * and arithmetic nobody can run in a test is arithmetic nobody can trust.
 *
 * Money is in minor units throughout, like every other amount in the
 * codebase, and stays integral: these functions sum and compare, they never
 * divide money.
 */
const DAY_MS = 86_400_000;
/**
 * Statuses that hold a car.
 *
 * A reservation nobody has accepted yet, and one whose payment has not
 * landed, are both still offers — counting their days as occupancy would
 * report a fleet as busy on the strength of requests that may never
 * happen. Cancelled is self-evidently not occupancy.
 */
const COMMITTED = ['CONFIRMED', 'ACTIVE', 'COMPLETED'];
/**
 * When a hire's money is counted.
 *
 * On the day the car comes back, not the day it was booked. A booking taken
 * in March for a hire in June is June's earnings, and treating it as March's
 * would let a single long reservation inflate a month that saw no cars
 * move. `Booking` carries no `completedAt`, so the drop-off is the closest
 * honest stand-in — and on a completed hire the two are the same day.
 */
function earnedAt(hire) {
    return new Date(hire.dropoffAt).getTime();
}
/** The last `days` days, ending now. */
export function windowOf(days, now = new Date()) {
    return { from: new Date(now.getTime() - days * DAY_MS), to: now };
}
/**
 * The equally-long span immediately before this one — what a delta is
 * measured against. Thirty days is compared with the thirty before it, so
 * the comparison never straddles a boundary of a different size.
 */
export function precedingWindow(window) {
    const span = window.to.getTime() - window.from.getTime();
    return { from: new Date(window.from.getTime() - span), to: new Date(window.from) };
}
function within(at, window) {
    return at >= window.from.getTime() && at < window.to.getTime();
}
/** Milliseconds two spans share. Zero when they do not touch. */
function overlapMs(start, end, window) {
    const from = Math.max(start, window.from.getTime());
    const to = Math.min(end, window.to.getTime());
    return Math.max(0, to - from);
}
/** Completed earnings realised inside the window, in minor units. */
export function earningsIn(hires, window) {
    return hires.reduce((total, hire) => hire.status === 'COMPLETED' && within(earnedAt(hire), window)
        ? total + hire.partnerEarnings
        : total, 0);
}
/** Hires that finished inside the window. */
export function completedIn(hires, window) {
    return hires.filter((hire) => hire.status === 'COMPLETED' && within(earnedAt(hire), window))
        .length;
}
/**
 * How much of the fleet's available time was sold, as a fraction of 1.
 *
 * Numerator is the part of each committed hire that falls *inside* the
 * window, so a fortnight's hire straddling the edge contributes only the
 * days on this side of it. Denominator is every car's whole window, which
 * means a car added yesterday drags the number down — the alternative,
 * pro-rating each car from the day it was listed, would quietly flatter a
 * fleet that is mostly new, and the honest reading is the one an owner can
 * act on.
 *
 * Returns null rather than zero for an empty fleet: no cars is not
 * nought per cent busy, it is a question with no answer.
 */
export function occupancyIn(hires, window, fleetSize) {
    if (fleetSize <= 0)
        return null;
    const soldMs = hires.reduce((total, hire) => COMMITTED.includes(hire.status)
        ? total +
            overlapMs(new Date(hire.pickupAt).getTime(), new Date(hire.dropoffAt).getTime(), window)
        : total, 0);
    const availableMs = (window.to.getTime() - window.from.getTime()) * fleetSize;
    return Math.min(1, soldMs / availableMs);
}
/**
 * Period-on-period change as a fraction: 0.25 is a quarter up.
 *
 * Null when the previous period was nothing. Growth from zero has no
 * percentage — every candidate answer is either infinity or a lie — and a
 * caller that gets null is expected to show the figure without a delta
 * rather than to print "+100%".
 */
export function changeRatio(current, previous) {
    if (previous === 0)
        return null;
    return (current - previous) / Math.abs(previous);
}
/**
 * Which calendar unit a window of this length should be drawn in.
 *
 * Bar charts are read at a glance, and the glance wants somewhere between
 * about eight and about fifteen marks: ninety daily bars are a texture, and
 * four monthly ones are not a trend.
 */
export function bucketUnitFor(window) {
    const days = (window.to.getTime() - window.from.getTime()) / DAY_MS;
    if (days <= 31)
        return 'day';
    if (days <= 120)
        return 'week';
    return 'month';
}
/** Monday of the week `date` falls in, at midnight local. */
function startOfWeek(date) {
    const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    // getDay() is Sunday-based; Monday-based weeks are what a hire book runs on.
    const shift = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - shift);
    return start;
}
function startOfUnit(date, unit) {
    if (unit === 'month')
        return new Date(date.getFullYear(), date.getMonth(), 1);
    if (unit === 'week')
        return startOfWeek(date);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
function nextUnit(date, unit) {
    if (unit === 'month')
        return new Date(date.getFullYear(), date.getMonth() + 1, 1);
    if (unit === 'week')
        return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 7);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
}
/**
 * Earnings over the window, split on calendar boundaries.
 *
 * Calendar rather than equal slices of the span: a bar labelled "March" has
 * to be March, and a partner comparing two bars is comparing two months
 * they recognise. The first bucket therefore starts before the window does
 * — the hires it counts are still only the ones inside it.
 *
 * Buckets with nothing in them are kept. A gap in trade is the most
 * informative thing a series can show, and dropping empty rows would draw
 * a busy fortnight and a dead one as neighbours of equal width.
 */
export function earningsSeries(hires, window, unit = bucketUnitFor(window)) {
    const buckets = [];
    for (let start = startOfUnit(window.from, unit); start < window.to; start = nextUnit(start, unit)) {
        buckets.push({ start, end: nextUnit(start, unit), value: 0 });
    }
    for (const hire of hires) {
        if (hire.status !== 'COMPLETED')
            continue;
        const at = earnedAt(hire);
        if (!within(at, window))
            continue;
        /*
         * Buckets are contiguous and ordered, so the last one that has already
         * started is the one this hire belongs to. Walked backwards rather than
         * with `findLast`, which is ES2023 and this package compiles to ES2022.
         */
        for (let index = buckets.length - 1; index >= 0; index -= 1) {
            const bucket = buckets[index];
            if (bucket && at >= bucket.start.getTime()) {
                bucket.value += hire.partnerEarnings;
                break;
            }
        }
    }
    return buckets;
}
/**
 * What each car earned in the window, best first.
 *
 * Cars that earned nothing are left out: this answers "which of these is
 * carrying the fleet", and a tail of zeroes only makes it longer. The
 * occupancy figure is where an idle car shows up.
 */
export function earningsByVehicle(hires, window) {
    const byVehicle = new Map();
    for (const hire of hires) {
        if (hire.status !== 'COMPLETED' || !within(earnedAt(hire), window))
            continue;
        const row = byVehicle.get(hire.vehicleId) ?? { vehicleId: hire.vehicleId, amount: 0, hires: 0 };
        row.amount += hire.partnerEarnings;
        row.hires += 1;
        byVehicle.set(hire.vehicleId, row);
    }
    return [...byVehicle.values()]
        .filter((row) => row.amount > 0)
        .sort((a, b) => b.amount - a.amount);
}
/**
 * How many hires sit in each status, over the whole book rather than a
 * window — a request waiting to be answered is waiting now, whenever it
 * was made.
 */
export function statusMix(hires) {
    const mix = {
        PENDING: 0,
        AWAITING_PAYMENT: 0,
        CONFIRMED: 0,
        ACTIVE: 0,
        COMPLETED: 0,
        CANCELLED: 0,
    };
    for (const hire of hires)
        mix[hire.status] += 1;
    return mix;
}
